import { TextSegment } from 'bellmere/validations/document/TextSegment.d';

/**
 * Handles the edge case where matched text is a bare text node (direct child of document root)
 * rather than being wrapped in a block-level element like a paragraph.
 *
 * This can occur when documents have malformed HTML structure where text exists outside
 * of proper paragraph tags.
 *
 * Strategy:
 * 1. Check adjacent paragraphs for matching text
 * 2. Reuse an empty/short adjacent paragraph
 * 3. Create a new paragraph wrapper
 * 4. Search all paragraphs in the document (last resort)
 */

/**
 * Normalizes text by collapsing whitespace and trimming
 */
const normalizeText = (text: string): string => text.replace(/\s+/g, ' ').trim();

/**
 * Finds the next element sibling, skipping text/comment nodes
 */
const findNextElementSibling = (node: Node): Element | null => {
  let { nextSibling } = node;
  while (nextSibling && nextSibling.nodeType !== Node.ELEMENT_NODE) {
    nextSibling = nextSibling.nextSibling;
  }
  return nextSibling as Element | null;
};

/**
 * Finds the previous element sibling, skipping text/comment nodes
 */
const findPreviousElementSibling = (node: Node): Element | null => {
  let prevSibling = node.previousSibling;
  while (prevSibling && prevSibling.nodeType !== Node.ELEMENT_NODE) {
    prevSibling = prevSibling.previousSibling;
  }
  return prevSibling as Element | null;
};

/**
 * Checks if a paragraph element contains the matched text
 */
const paragraphContainsText = (
  paragraph: HTMLElement,
  highlightText: string,
  matchLength: number = 50
): boolean => {
  const paragraphText = normalizeText(paragraph.textContent || '');
  const matchStart = normalizeText(highlightText).substring(0, matchLength);
  return paragraphText.includes(matchStart);
};

/**
 * Collects all nodes between start and end elements that are direct children of parent
 */
const collectNodesToWrap = (
  startElement: Node,
  endElement: Node | null,
  parent: Node
): Node[] => {
  const nodesToWrap: Node[] = [];
  let currentNode: Node | null = startElement;

  while (currentNode && currentNode !== endElement) {
    if (currentNode.parentNode === parent) {
      nodesToWrap.push(currentNode);
    }
    currentNode = currentNode.nextSibling;
  }

  if (endElement && endElement.parentNode === parent) {
    nodesToWrap.push(endElement);
  }

  return nodesToWrap;
};

/**
 * Attempts to find an adjacent paragraph that contains the matched text
 */
const findAdjacentParagraphWithText = (
  targetNode: Node,
  highlightText: string
): HTMLElement | null => {
  const nextSibling = findNextElementSibling(targetNode);
  if (nextSibling && (nextSibling as HTMLElement).tagName === 'P') {
    if (paragraphContainsText(nextSibling as HTMLElement, highlightText)) {
      return nextSibling as HTMLElement;
    }
  }

  const prevSibling = findPreviousElementSibling(targetNode);
  if (prevSibling && (prevSibling as HTMLElement).tagName === 'P') {
    if (paragraphContainsText(prevSibling as HTMLElement, highlightText)) {
      return prevSibling as HTMLElement;
    }
  }

  return null;
};

/**
 * Attempts to reuse an empty or short adjacent paragraph by moving nodes into it
 */
const tryReuseEmptyParagraph = (
  nextSibling: Element | null,
  nodesToWrap: Node[]
): HTMLElement | null => {
  if (!nextSibling || (nextSibling as HTMLElement).tagName !== 'P') {
    return null;
  }

  const paragraph = nextSibling as HTMLElement;
  const text = normalizeText(paragraph.textContent || '');

  if (text.length === 0 || text.length < 20) {
    nodesToWrap.reverse().forEach((node) => {
      paragraph.insertBefore(node, paragraph.firstChild);
    });
    return paragraph;
  }

  return null;
};

/**
 * Creates a new paragraph element and wraps the nodes
 */
const createParagraphWrapper = (
  nodesToWrap: Node[]
): HTMLElement | null => {
  if (nodesToWrap.length === 0 || !nodesToWrap[0].parentNode) {
    return null;
  }

  const newParagraph = document.createElement('p');
  newParagraph.classList.add('ohe');

  nodesToWrap[0].parentNode.insertBefore(newParagraph, nodesToWrap[0]);

  nodesToWrap.forEach((node) => {
    newParagraph.appendChild(node);
  });

  return newParagraph;
};

/**
 * Searches all paragraphs in the document for the best match
 * First tries exact content match, then falls back to length-based heuristic
 */
const searchAllParagraphsForMatch = (
  documentRoot: HTMLElement,
  highlightText: string
): HTMLElement | null => {
  const allParagraphs = Array.from(documentRoot.querySelectorAll('p'));
  const normalizedHighlights = normalizeText(highlightText);
  const matchStart = normalizedHighlights.substring(0, 50);
  let bestMatch: { paragraph: HTMLElement; score: number } | null = null;

  for (let i = 0; i < allParagraphs.length; i += 1) {
    const paragraph = allParagraphs[i];
    const paragraphText = paragraph.textContent || '';
    const normalizedParagraph = normalizeText(paragraphText);

    // Skip very short paragraphs - they won't match the exact content check,
    // and aren't eligible for the length-based heuristic anyway (requires >500 chars)
    if (normalizedParagraph.length >= 50) {
      // Exact match: if paragraph contains the start of the highlight text, use it immediately
      if (normalizedParagraph.includes(matchStart)) {
        return paragraph;
      }

      // Fallback heuristic: track paragraphs with similar length as potential matches
      // Only consider substantial paragraphs (>500 chars) with length difference <100 chars
      const lengthDiff = Math.abs(normalizedParagraph.length - normalizedHighlights.length);
      if (lengthDiff < 100 && normalizedParagraph.length > 500) {
        // Smaller length difference = better match
        if (!bestMatch || lengthDiff < bestMatch.score) {
          bestMatch = { paragraph, score: lengthDiff };
        }
      }
    }
  }

  return bestMatch?.paragraph || null;
};

/**
 * Handles the case where text is a bare text node (direct child of document root)
 * Attempts to find or create a suitable paragraph wrapper
 */
export default function handleBareTextNode(
  targetNode: Node,
  textSegment: TextSegment,
  documentRoot: HTMLElement,
  highlightText: string
): HTMLElement | null {
  if (targetNode.parentNode !== documentRoot) {
    return null;
  }

  const adjacentParagraph = findAdjacentParagraphWithText(targetNode, highlightText);
  if (adjacentParagraph) {
    return adjacentParagraph;
  }

  const nodesToWrap = collectNodesToWrap(
    textSegment.startElement,
    textSegment.endElement,
    documentRoot
  );

  const nextSibling = findNextElementSibling(targetNode);
  const reusedParagraph = tryReuseEmptyParagraph(nextSibling, nodesToWrap);
  if (reusedParagraph) {
    return reusedParagraph;
  }

  const newParagraph = createParagraphWrapper(nodesToWrap);
  if (newParagraph) {
    return newParagraph;
  }

  return searchAllParagraphsForMatch(documentRoot, highlightText);
}
